import React, { useCallback, useEffect, useRef } from 'react';

import { ButtonWithSpinner } from '@eika/button-with-spinner';

import { paymentFromSelector, useAccounts } from '../../../fetchHooks/useAccount';
import type { Account, AccountId } from '../../../generated-types';
import { useFormContext } from '../../../hooks/useForm';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useTranslation from '../../../hooks/useTranslation';
import { isValidAccountPaymentFrom } from '../../../services/accountFilters';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import { DismissKeyboardStickyFooter } from '../../shared/DismissKeyboardStickyFooter';
import AccountsEmptyInfo from '../AccountsEmptyInfo';
import { PaymentRetryContext } from '../PaymentRetry';
import TransactionResultInfo from '../TransactionResultInfo';
import RecurringPaymentFields from './RecurringPaymentFields';
import createRecurringPaymentRequest from './createRecurringPaymentRequest';
import { RecurringPaymentAction, RecurringPaymentActionType } from './form/actions';
import { RecurringPaymentState } from './form/state';
import { RecurringPaymentError } from './form/validate';

export const ANALYTICS_CATEGORY = 'recurring-payment';

interface Props {
    onClose: () => void;
    account?: Account;
}

const CreateRecurringPaymentForm: React.FC<Props> = ({ onClose, account }) => {
    const translated = useTranslation();
    const fromAccountId = useRef<AccountId>();

    const { data: accounts, ...accountState } = useAccounts({ select: paymentFromSelector });
    const {
        signingStatus,
        signingError,
        signingOperationPending,
        handleCancelSigningFlow,
        startSigning,
        showPaymentRetry,
    } = useSigningContext(createRecurringPaymentRequest());

    const { state, handleChange, checkFormValidity } = useFormContext<
        RecurringPaymentState,
        RecurringPaymentAction,
        RecurringPaymentError
    >();

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning(state);
            }
        },
        [checkFormValidity, startSigning, state],
    );

    useEffect(() => {
        if (account && !state.fromAccount) {
            if (isValidAccountPaymentFrom(account)) {
                handleChange({
                    type: RecurringPaymentActionType.FROM_ACCOUNT_SELECTED,
                    payload: account.accountNumber,
                });
            }
        }
    }, [account, handleChange, state.fromAccount]);

    const showPaymentResult = useShowPaymentResult();

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (showPaymentResult) {
        return (
            <TransactionResultInfo
                status={signingStatus}
                error={signingError}
                type="RECURRING_PAYMENT"
                retry={signingError ? handleCancelSigningFlow : onClose}
                fromAccountId={fromAccountId.current}
                onClose={onClose}
                paymentDate={state.firstPaymentDate}
            />
        );
    }

    if (accountState.isFetched && !accounts?.length) {
        return <AccountsEmptyInfo title={translated.accountsEmptyInfo.recurringPaymentTitle} />;
    }

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <h2 className="create-recurring-payment-form__title">{translated.recurringPayments.recurringPayment}</h2>
            <TransactionForm eightCol onSubmit={onSubmit}>
                <RecurringPaymentFields locked={signingOperationPending} />
                <TransactionSubmitWrapper>
                    <ButtonWithSpinner
                        buttonType="positive"
                        className="create-recurring-payment-form__submit"
                        type="submit"
                        showSpinner={signingOperationPending}
                        data-testid="payment-button"
                        disabled={(state?.toAccount?.kidRequired && state.note.length === 0) || signingOperationPending}
                    >
                        {translated.recurringPayments.createPayment}
                    </ButtonWithSpinner>
                </TransactionSubmitWrapper>
            </TransactionForm>
            <DismissKeyboardStickyFooter />
        </SigningContextFlow>
    );
};

export default CreateRecurringPaymentForm;
